import React, {
  useRef,
  RefObject,
  forwardRef,
  useCallback,
  useMemo,
  useEffect,
} from 'react'
import { Box, BoxProps } from '@chakra-ui/react'
import { Formik, Form, FormikHelpers, FormikValues, FormikProps } from 'formik'

import { FormManagerContext } from '.'

type FormManagerProps<Values = FormikValues> = Omit<
  BoxProps,
  'children' | 'onSubmit'
> & {
  children: (props: FormikProps<FormikValues>) => React.ReactNode
} & {
  initialValues: Values
  validationSchema?: any
  onSubmit: (
    values: Values,
    formikHelpers: FormikHelpers<Values>
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
  ) => void | Promise<unknown>
  action?: string
}

type FieldMap = Map<string, RefObject<HTMLElement>>
const ScrollToFirstError = ({
  errors,
  touched,
  fields,
  nonce,
}: {
  errors: FormikProps<FormikValues>['errors']
  touched: FormikProps<FormikValues>['touched']
  fields: FieldMap
  // We only want to scroll when the form is submitted, so we pass the nonce to
  // use as the effect's dep
  // CAVEAT: Becuase of the order of formik's effects, the nonce will change
  // before the `touched` prop is updated — meaning that we won't scroll to the
  // first error the first time an empty form is submitted
  nonce: number
}) => {
  const erroredFields = Object.keys(errors)

  useEffect(() => {
    for (const [key, value] of fields) {
      // Because `fields` is a Map, `includes()` runs thorugh the values
      // sequentially, so the first erorred field we find will be the one
      // highest up in the form
      if (erroredFields.includes(key)) {
        // We only want to scroll to the field if the user has touched it
        if (value.current && touched[key]) {
          value.current.scrollIntoView({ behavior: 'smooth' })
          break
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonce])

  return null
}

export const FormManager = forwardRef<HTMLFormElement, FormManagerProps>(
  (
    { initialValues, children, onSubmit, validationSchema, action, ...props },
    ref
  ) => {
    const fieldRefs = useRef<FieldMap>(new Map())

    const register = useCallback(
      (name: string, ref: RefObject<HTMLDivElement>) => {
        if (fieldRefs.current.has(name)) return
        fieldRefs.current.set(name, ref)
      },
      [fieldRefs]
    )
    const contextValue = useMemo(() => ({ register }), [register])

    return (
      <Box {...props}>
        <FormManagerContext.Provider value={contextValue}>
          <Formik
            onSubmit={onSubmit}
            initialValues={initialValues}
            validationSchema={validationSchema}
          >
            {(formikBag) => (
              <>
                <ScrollToFirstError
                  errors={formikBag.errors}
                  touched={formikBag.touched}
                  fields={fieldRefs.current}
                  nonce={formikBag.submitCount}
                />
                <Form
                  ref={ref}
                  method={action ? 'POST' : undefined}
                  action={action}
                >
                  {children(formikBag)}
                </Form>
              </>
            )}
          </Formik>
        </FormManagerContext.Provider>
      </Box>
    )
  }
)
